import { components } from "@certn/api/generated-schemas/client-portal";

import {
  isCheckStandaloneDisabled,
  getRequiredChecksFromSelectedChecks,
} from "../../order-utils";
import { CheckData } from "../../types";

interface UseCheckListProps {
  availableChecks: CheckData[];
  order: any;
  bundleChecks: string[];
  handleCheckSelect: (check: CheckData) => void;
  handleUpdateArguments: (
    checkArguments: Record<string, unknown>,
    check: CheckData,
    selectedOptions:
      | components["schemas"]["UmbrellaCheckSelectOption"][]
      | undefined
  ) => void;
  group: string | null;
}

function isCheckWithError(check: CheckData) {
  return Boolean(check.check_has_ordering_error_for_customer_group);
}

function isCheckRequired(order: any, check: CheckData) {
  return getRequiredChecksFromSelectedChecks(order.checks).includes(check.type);
}

function isCheckInSelectedBundle(bundleChecks: string[], check: CheckData) {
  return bundleChecks.includes(check.type);
}

function isCheckSelected(
  order: any,
  check: CheckData,
  isCheckInSelectedBundle: boolean
) {
  return (
    !!order.checks.find(({ key }: { key: string }) => key === check.type) ||
    isCheckInSelectedBundle
  );
}

function getBundledSelectedArg(order: any, check: CheckData) {
  let bundledSelectedArg: { select_options?: any } | undefined;
  order.bundles.forEach(
    (bundle: { checks?: { type: string; select_options?: any }[] }) => {
      const found = bundle.checks?.find(
        (orderCheck: { type: string }) => orderCheck.type === check.type
      );
      if (found) bundledSelectedArg = found;
    }
  );
  return bundledSelectedArg;
}

function getCheckArguments(order: any, check: CheckData) {
  // First check in order.checks
  const directCheckArgs = order.checks.find(
    (orderCheck: { key: string; check_arguments?: any }) =>
      orderCheck.key === check.type
  )?.check_arguments;

  if (directCheckArgs) {
    return directCheckArgs;
  }

  // If not found, check in bundle checks
  for (const bundle of order.bundles || []) {
    const bundleCheck = bundle.checks?.find(
      (orderCheck: { type: string; check_arguments?: any }) =>
        orderCheck.type === check.type
    );
    if (bundleCheck?.check_arguments) {
      return bundleCheck.check_arguments;
    }
  }

  return undefined;
}

// https://certn.atlassian.net/browse/CHCKS-246
// TODO: there is a known issue here, if the select options are populated via an endpoint then this function will fail to find them
function getCurrentSelection(check: CheckData, checkArguments: any) {
  // Handle EXPLICIT_ORDERING format where explicit_check_type is an array
  if (
    checkArguments?.ordering_type === "EXPLICIT_ORDERING" &&
    Array.isArray(checkArguments?.explicit_check_type)
  ) {
    const matchingOptions = check.select_options?.filter((option: any) => {
      const optionValue = Array.isArray(option.value)
        ? option.value
        : [option.value];
      return optionValue.some((v: string) =>
        checkArguments.explicit_check_type.includes(v)
      );
    });
    if (matchingOptions && matchingOptions.length > 0) {
      return matchingOptions;
    }
  }

  // Handle template_ids array (e.g. CQ bundles with template-based selection)
  if (
    checkArguments?.template_ids &&
    Array.isArray(checkArguments.template_ids)
  ) {
    const matchingOptions = check.select_options?.filter((option: any) => {
      return checkArguments.template_ids.some(
        (tid: string) => String(tid) === String(option.value)
      );
    });
    if (matchingOptions && matchingOptions.length > 0) {
      return matchingOptions;
    }
  }

  // Single-select: handle both scalar and array argument values
  return check.select_options?.find((option: any) => {
    const argValue = checkArguments?.[option.select_field_argument];
    if (argValue === undefined || argValue === null) return false;
    if (Array.isArray(argValue)) {
      return argValue.some((v: unknown) => String(v) === String(option.value));
    }
    return String(option.value) === String(argValue);
  });
}

export function useCheckList({
  availableChecks,
  order,
  bundleChecks,
  handleCheckSelect,
  handleUpdateArguments,
  handleUpdateCheckAddOns,
  group,
}: UseCheckListProps & {
  handleUpdateCheckAddOns?: (
    checkType: string,
    checkArguments: Record<string, unknown>
  ) => void;
}) {
  return availableChecks.map((check: CheckData) => {
    const checkHasError = isCheckWithError(check);
    const checkIsRequired = isCheckRequired(order, check);
    const checkIsStandaloneDisabled = isCheckStandaloneDisabled(
      order.checks,
      check
    );
    const checkIsInBundle = isCheckInSelectedBundle(bundleChecks, check);
    const checkIsSelected = isCheckSelected(order, check, checkIsInBundle);

    const onUpdateSelection = (
      checkArguments: Record<string, unknown>,
      selectedOptions:
        | components["schemas"]["UmbrellaCheckSelectOption"][]
        | undefined
    ) => {
      handleUpdateArguments(checkArguments, check, selectedOptions);
    };

    const bundledSelectedArg = getBundledSelectedArg(order, check);
    const checkArguments = getCheckArguments(order, check);
    const currentSelection = getCurrentSelection(check, checkArguments);

    return {
      check,
      checkHasError,
      checkIsRequired,
      checkIsStandaloneDisabled,
      checkIsInBundle,
      checkIsSelected,
      onUpdateSelection,
      handleUpdateCheckAddOns: (checkArguments: Record<string, unknown>) => {
        handleUpdateCheckAddOns?.(check.type, checkArguments);
      },
      bundledSelectedArg,
      checkArguments,
      currentSelection,
      handleCheckSelect,
      group,
    };
  });
}
